import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Restaurant as RecipeIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Description as ContractIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  getChefDashboard,
  getChefRecipes,
  ChefDashboardData,
  ChefRecipe,
} from './chefApi';

const ChefDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [dashboard, setDashboard] = useState<ChefDashboardData | null>(null);
  const [recipes, setRecipes] = useState<ChefRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardData, recipeData] = await Promise.all([
        getChefDashboard(),
        getChefRecipes({ limit: 100 }),
      ]);
      setDashboard(dashboardData);
      setRecipes(recipeData);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('not_a_chef');
      } else {
        setError(err.response?.data?.error || 'Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleCopyCode = () => {
    if (dashboard?.chef.promo_code) {
      navigator.clipboard.writeText(dashboard.chef.promo_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'active': return 'success';
      case 'approved': return 'info';
      case 'pending': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getSourceTypeLabel = (sourceType: string | null) => {
    switch (sourceType) {
      case 'chef_curated': return 'Curated';
      case 'ai_generated': return 'AI';
      default: return sourceType || 'Unknown';
    }
  };

  const getSourceTypeColor = (sourceType: string | null): 'primary' | 'default' => {
    return sourceType === 'chef_curated' ? 'primary' : 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !dashboard) {
    if (error === 'not_a_chef') {
      return (
        <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', textAlign: 'center', mt: 8 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Chef Program
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            It looks like you're not part of our chef program yet. If you're interested in contributing recipes, reach out to us!
          </Typography>
          <Button
            variant="contained"
            href="mailto:support@snackdapp.com"
          >
            Contact Us
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!dashboard) {
    return null;
  }

  if (!dashboard.chef.terms_accepted_at) {
    return <Navigate to="/chef-agreement" replace />;
  }

  const { chef, recipes: recipeStats, usage: usageStats } = dashboard;

  // Contract expiry warning
  const contractEndDate = chef.contract_end_date ? new Date(chef.contract_end_date) : null;
  const daysUntilExpiry = contractEndDate
    ? Math.ceil((contractEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Chef Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back, {chef.name}!
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={chef.status}
            color={getStatusColor(chef.status)}
            size="small"
          />
          <IconButton onClick={fetchDashboard} size="small">
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {chef.status === 'suspended' && (
        <Alert severity="error" variant="filled" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Your chef account has been suspended
          </Typography>
          <Typography variant="body2">
            Your promo code has been deactivated and recipes will no longer be attributed to your profile. If you have questions, contact support@snackdapp.com.
          </Typography>
        </Alert>
      )}

      {/* Promo Code Card */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #032736 0%, #0a4d6e 100%)',
          color: '#F78764',
        }}
      >
        <Typography variant="overline" sx={{ opacity: 0.8, color: 'inherit' }}>
          Your Promo Code
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          mt={1}
        >
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{
              letterSpacing: 2,
              fontFamily: 'monospace',
              color: 'inherit',
            }}
          >
            {chef.promo_code || 'Not assigned'}
          </Typography>
          {chef.promo_code && (
            <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
              <IconButton
                onClick={handleCopyCode}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                }}
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Typography variant="body2" sx={{ mt: 2, opacity: 0.8, color: 'inherit' }}>
          Share this code with your audience. They get an extended 30-day free trial!
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.6, color: 'inherit' }}>
          Used {chef.code_uses || 0} times
        </Typography>
      </Paper>

      {/* Contract Info Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <ContractIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Contract Info
          </Typography>
          <Chip label={chef.tier} size="small" color="primary" variant="outlined" />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Recipe Rate</Typography>
            <Typography fontWeight="medium">{formatCurrency(chef.recipe_rate)} / recipe / year</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Contract End Date</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontWeight="medium">
                {contractEndDate ? formatDate(chef.contract_end_date) : 'N/A'}
              </Typography>
              {daysUntilExpiry !== null && daysUntilExpiry <= 30 && daysUntilExpiry > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label={`${daysUntilExpiry} days left`}
                  size="small"
                  color="warning"
                />
              )}
              {daysUntilExpiry !== null && daysUntilExpiry <= 0 && (
                <Chip label="Expired" size="small" color="error" />
              )}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Stats Grid */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <RecipeIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Total Recipes
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold">
                {recipeStats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <TrendingUpIcon color="info" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Meal Plan Appearances
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {usageStats.total_meal_appearances.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recipe List Table */}
      {recipes.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Your Recipes
          </Typography>
          <TableContainer>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  {!isMobile && <TableCell>Cuisine</TableCell>}
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Used In Plans</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipes.map((recipe) => (
                  <TableRow key={recipe.recipe_id}>
                    <TableCell>{recipe.name}</TableCell>
                    {!isMobile && <TableCell>{recipe.cuisine || '-'}</TableCell>}
                    <TableCell>
                      <Chip
                        label={getSourceTypeLabel(recipe.source_type)}
                        size="small"
                        color={getSourceTypeColor(recipe.source_type)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{recipe.meal_plan_count}</TableCell>
                    <TableCell>{formatDate(recipe.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default ChefDashboard;
